/**
 * structure_seat — placed deployed-structure seats + keep-out discs.
 *
 * StructureRenderModule seats structures footprint-aware (terrainSeat over the
 * scaled model bbox — high-relief audit 2026-08-05); DecorationModule composes
 * item transforms on the SAME parent origin, so it must read the exact seated
 * Y, not re-derive a single-point groundY (the two disagree on slopes by the
 * footprint-min delta, shearing decor off its shelf). The entry also carries
 * the structure's position + footprint radius so versona rendering can project
 * NPC bodies out of deed buildings (server NPC movement is geometry-blind).
 * Keyed by structureId; absent → the structure isn't placed yet.
 */
import { seatingOffset, pivotBodyPos, type Box3Like, type Vec3Like } from './structure_pivot.js';

export interface StructureSeat { y: number; x: number; z: number; r: number }

export const structureSeats = new Map<string, StructureSeat>();

// ── THE SOLID FRAME (2026-09-23) ──────────────────────────────────────────────
// A structure solid's yaw is THREE's rotation about Y (every producer composes
// its instance with `rotation.y = yaw` / `setFromAxisAngle(up, yaw)`), so
//   local (x, z) → world (x·cos yaw + z·sin yaw, −x·sin yaw + z·cos yaw)
// and the inverse is the transpose: lx = dx·cos + (−dz)·sin … The decal face
// maths and the birds' roof perches used the STANDARD 2D rotation instead — a
// MIRRORED frame that drew a diagonal non-square box at twice its angle (the
// same bug `structure_solids.toLocal` fixed on 2026-09-16). ONE pair here,
// pinned against `structureHitAt` by verify:structure-frame.
export interface SolidFrame { x: number; z: number; yaw: number }

/** World XZ → a solid's local (yaw-aligned) footprint frame — three's convention. */
export function solidToLocal(s: SolidFrame, wx: number, wz: number): { lx: number; lz: number } {
  // Inverse of three's rotation about Y: R(yaw)ᵀ = [[c, −s], [s, c]] applied to (dx, dz).
  const dx = wx - s.x, dz = wz - s.z;
  const c = Math.cos(s.yaw), sn = Math.sin(s.yaw);
  return { lx: dx * c - dz * sn, lz: dx * sn + dz * c };
}

/** A solid's local footprint point → world XZ — three's rotation about Y. */
export function solidToWorld(s: SolidFrame, lx: number, lz: number): { x: number; z: number } {
  // R(yaw) = [[c, s], [−s, c]]: local x → (c, −s), local z → (s, c).
  const c = Math.cos(s.yaw), sn = Math.sin(s.yaw);
  return { x: s.x + lx * c + lz * sn, z: s.z - lx * sn + lz * c };
}

/** A local direction (nx, nz) turned to world by a solid's yaw (a normal — no translation). */
export function solidDirToWorld(s: SolidFrame, nx: number, nz: number): { x: number; z: number } {
  const c = Math.cos(s.yaw), sn = Math.sin(s.yaw);
  return { x: nx * c + nz * sn, z: -nx * sn + nz * c };
}

// ── THE DEED PLACEMENT (2026-09-23) ───────────────────────────────────────────
// ONE record for a deployed structure's render AND its collider. The render
// composes T(pos)·R(yaw)·S(scale)·T(off) (off = the Edit-Mode seating law,
// `structure_pivot.seatingOffset`: null for a well-pivoted model — every deed
// GLB measured 2026-09-23 has its bbox bottom-centre at the origin ±3 mm, so
// nothing placed today moves); the collider proxy body is the SAME frame
// (`pivotBodyPos`), the box fallback is centred on the RENDERED bbox centre
// (the old fallback assumed minY = 0 and cx = cz = 0 at posX/posZ), and the
// keep-out seat is the SEATED footprint centre.
export interface DeedPlacement {
  /** The model-origin position the render composes; yaw about Y; uniform scale. */
  x: number; y: number; z: number; yaw: number; scale: number;
  /** Natural-scale seating offset applied INSIDE the placement (null = the authored pivot). */
  off: Vec3Like | null;
  /** Rapier body transform for the mesh-space collider proxy — where the render puts the model origin. */
  proxyT: { x: number; y: number; z: number; yaw: number; scale: number };
  /** AABB fallback: PRE-SCALED half extents + the box body (scale 1 — scaleProxy never scales a box). */
  boxHalf: [number, number, number];
  boxT: { x: number; y: number; z: number; yaw: number; scale: number };
  /** The seated footprint centre (world XZ) — the keep-out seat / enterable anchor. */
  footX: number; footZ: number;
  /** Scaled bbox size. */
  w: number; h: number; d: number;
}

export function deedPlacement(key: string, posX: number, y: number, posZ: number, yaw: number, scale: number, bb: Box3Like): DeedPlacement {
  const off = seatingOffset(key, bb);
  const w = (bb.max.x - bb.min.x) * scale, h = (bb.max.y - bb.min.y) * scale, d = (bb.max.z - bb.min.z) * scale;
  const cx = (bb.min.x + bb.max.x) / 2, cy = (bb.min.y + bb.max.y) / 2, cz = (bb.min.z + bb.max.z) / 2;
  // The bbox centre in the placement's own frame after seating, scaled — the box's local centre.
  const lx = (cx + (off?.x ?? 0)) * scale, ly = (cy + (off?.y ?? 0)) * scale, lz = (cz + (off?.z ?? 0)) * scale;
  const bw = solidToWorld({ x: posX, z: posZ, yaw }, lx, lz);
  const origin = { x: posX, y, z: posZ };
  const proxyPos = off ? pivotBodyPos(origin, yaw, scale, off) : origin;
  return {
    x: posX, y, z: posZ, yaw, scale, off,
    proxyT: { x: proxyPos.x, y: proxyPos.y, z: proxyPos.z, yaw, scale },
    boxHalf: [Math.max(0.05, w / 2), Math.max(0.05, h / 2), Math.max(0.05, d / 2)],
    boxT: { x: bw.x, y: y + ly, z: bw.z, yaw, scale: 1 },
    footX: bw.x, footZ: bw.z,
    w, h, d,
  };
}

// ── ADMIN PLACEMENT COLLIDERS (2026-09-23) ────────────────────────────────────
// The Commons holds ~1,500 admin placements (fountains, kiosks, comms towers,
// whole civb_city6 structures) that players walked straight through. A
// placement is SOLID when its prefab is a STRUCTURE, or when its scaled bbox
// is at least PLACEMENT_SOLID_MIN_XZ in both horizontal extents and
// PLACEMENT_SOLID_MIN_H tall — a fountain centrepiece, a kiosk, a tower; never
// a bench or a lamp post (walkable street furniture).
export const PLACEMENT_SOLID_MIN_XZ = 2.5;
export const PLACEMENT_SOLID_MIN_H = 2;
/** A quaternion within this of a pure yaw is treated as a yaw (rad, |pitch| + |roll|). */
export const PLACEMENT_TILT_EPS = 0.02;

export function placementSolidEligible(prefabKey: string, w: number, h: number, d: number): boolean {
  if (prefabKey.startsWith('structure:')) return true;
  return w >= PLACEMENT_SOLID_MIN_XZ && d >= PLACEMENT_SOLID_MIN_XZ && h >= PLACEMENT_SOLID_MIN_H;
}

export interface QuatLike { x: number; y: number; z: number; w: number }

/** three's Euler 'YXZ' decomposition of a unit quaternion (yaw = .y; the tilt is |x| + |z|). */
export function quatYawTilt(q: QuatLike): { yaw: number; tilt: number } {
  const { x, y, z, w } = q;
  const m13 = 2 * (x * z + w * y), m23 = 2 * (y * z - w * x), m33 = 1 - 2 * (x * x + y * y);
  const m21 = 2 * (x * y + w * z), m22 = 1 - 2 * (x * x + z * z);
  const ex = Math.asin(-Math.max(-1, Math.min(1, m23)));
  let yaw: number, ez: number;
  if (Math.abs(m23) < 0.9999999) { yaw = Math.atan2(m13, m33); ez = Math.atan2(m21, m22); }
  else { yaw = Math.atan2(-(2 * (x * z - w * y)), 1 - 2 * (y * y + z * z)); ez = 0; } // three's gimbal branch
  return { yaw, tilt: Math.abs(ex) + Math.abs(ez) };
}

function quatApply(q: QuatLike, v: Vec3Like): Vec3Like {
  // v' = v + 2·qv × (qv × v + w·v)
  const tx = 2 * (q.y * v.z - q.z * v.y), ty = 2 * (q.z * v.x - q.x * v.z), tz = 2 * (q.x * v.y - q.y * v.x);
  return {
    x: v.x + q.w * tx + (q.y * tz - q.z * ty),
    y: v.y + q.w * ty + (q.z * tx - q.x * tz),
    z: v.z + q.w * tz + (q.x * ty - q.y * tx),
  };
}

export interface PlacementColliderPlan {
  /** 'proxy' = the cooked mesh-space proxy at the render's frame; 'box' = the seated, scaled bbox. */
  shape: 'proxy' | 'box';
  t: { x: number; y: number; z: number; yaw: number; scale: number };
  /** Box only (pre-scaled; scale 1 in `t`). */
  half: [number, number, number];
  /** True when the row carries pitch/roll: the box is then the world AABB of the tilted bbox (yaw 0). */
  tilted: boolean;
  w: number; h: number; d: number;
}

/**
 * The collider for one admin placement: row transform (a full quaternion and
 * a per-axis scale), the prefab's natural-scale bbox and whether a usable
 * proxy is in hand. Seated by the PIVOT LAW exactly like the instanced render
 * (`row × seatingOffset × part`). A proxy needs a uniform scale and a pure yaw
 * (Rapier statics here are yaw + uniform scale); anything else is a box — a
 * yawed one, or the world AABB of a tilted row. Null = not solid.
 */
export function placementColliderPlan(
  prefabKey: string, pos: Vec3Like, q: QuatLike, sc: Vec3Like, bb: Box3Like, proxyUsable: boolean,
): PlacementColliderPlan | null {
  const w = (bb.max.x - bb.min.x) * sc.x, h = (bb.max.y - bb.min.y) * sc.y, d = (bb.max.z - bb.min.z) * sc.z;
  if (!(w > 1e-3 && h > 1e-3 && d > 1e-3)) return null;
  if (!placementSolidEligible(prefabKey, w, h, d)) return null;
  const off = seatingOffset(prefabKey, bb) ?? { x: 0, y: 0, z: 0 };
  const { yaw, tilt } = quatYawTilt(q);
  const m = Math.max(Math.abs(sc.x), Math.abs(sc.y), Math.abs(sc.z), 1e-6);
  const uniform = Math.abs(sc.x - sc.y) <= 1e-3 * m && Math.abs(sc.y - sc.z) <= 1e-3 * m;
  const tilted = tilt >= PLACEMENT_TILT_EPS;
  if (proxyUsable && uniform && !tilted) {
    const p = pivotBodyPos(pos, yaw, sc.x, off);
    return { shape: 'proxy', t: { x: p.x, y: p.y, z: p.z, yaw, scale: sc.x }, half: [w / 2, h / 2, d / 2], tilted: false, w, h, d };
  }
  // The bbox centre in the row's frame after seating, per-axis scaled.
  const lc = { x: ((bb.min.x + bb.max.x) / 2 + off.x) * sc.x, y: ((bb.min.y + bb.max.y) / 2 + off.y) * sc.y, z: ((bb.min.z + bb.max.z) / 2 + off.z) * sc.z };
  if (!tilted) {
    const c = solidToWorld({ x: pos.x, z: pos.z, yaw }, lc.x, lc.z);
    return { shape: 'box', t: { x: c.x, y: pos.y + lc.y, z: c.z, yaw, scale: 1 }, half: [w / 2, h / 2, d / 2], tilted: false, w, h, d };
  }
  // Tilted: the world AABB of the eight seated, scaled, quaternion-rotated corners.
  const hx = w / 2, hy = h / 2, hz = d / 2;
  const mn = { x: Infinity, y: Infinity, z: Infinity }, mx = { x: -Infinity, y: -Infinity, z: -Infinity };
  for (let i = 0; i < 8; i++) {
    const v = quatApply(q, { x: lc.x + (i & 1 ? hx : -hx), y: lc.y + (i & 2 ? hy : -hy), z: lc.z + (i & 4 ? hz : -hz) });
    mn.x = Math.min(mn.x, v.x); mn.y = Math.min(mn.y, v.y); mn.z = Math.min(mn.z, v.z);
    mx.x = Math.max(mx.x, v.x); mx.y = Math.max(mx.y, v.y); mx.z = Math.max(mx.z, v.z);
  }
  return {
    shape: 'box',
    t: { x: pos.x + (mn.x + mx.x) / 2, y: pos.y + (mn.y + mx.y) / 2, z: pos.z + (mn.z + mx.z) / 2, yaw: 0, scale: 1 },
    half: [(mx.x - mn.x) / 2, (mx.y - mn.y) / 2, (mx.z - mn.z) / 2], tilted: true, w, h, d,
  };
}

export function setStructureSeat(structureId: string, seat: StructureSeat): void {
  structureSeats.set(structureId, seat);
}

export function clearStructureSeat(structureId: string): void {
  structureSeats.delete(structureId);
}

/** Project (x,z) out of every placed structure's keep-out disc (+margin). */
export function structureClearance(x: number, z: number, margin = 1.2): { x: number; z: number } | null {
  let moved = false;
  for (const s of structureSeats.values()) {
    const dx = x - s.x;
    const dz = z - s.z;
    const keep = s.r + margin;
    if (dx * dx + dz * dz >= keep * keep) continue;
    const d = Math.hypot(dx, dz);
    const ux = d > 1e-6 ? dx / d : 1;
    const uz = d > 1e-6 ? dz / d : 0;
    x = s.x + ux * keep;
    z = s.z + uz * keep;
    moved = true;
  }
  return moved ? { x, z } : null;
}
